import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { MagnifyingGlassIcon, XCircleIcon } from '@phosphor-icons/react';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { ActionBarItem } from './ActionBarItem';
import { useDebounce } from '@/hooks/useDebounce';

interface ActionBarSearchProps {
    value: string;
    onChange: (value: string) => void;
    onSubmit?: (value: string) => void;
    placeholder?: string;
    isExpanded: boolean;
    onExpandedChange: (expanded: boolean) => void;
}

const DEBOUNCE_DELAY = 400;

export function ActionBarSearch({
    value,
    onChange,
    onSubmit,
    placeholder,
    isExpanded,
    onExpandedChange,
}: ActionBarSearchProps) {
    const { t } = useTranslation(I18nNamespace.COMMON);
    const inputRef = useRef<HTMLInputElement>(null);
    const debouncedValue = useDebounce(value, DEBOUNCE_DELAY);

    const submittedValueRef = useRef<string | null>(null);
    useEffect(() => {
        if (submittedValueRef.current === debouncedValue) return;
        submittedValueRef.current = debouncedValue;
        onSubmit?.(debouncedValue);
    }, [debouncedValue, onSubmit]);

    const handleBlur = () => {
        if (!value) {
            onExpandedChange(false);
        }
    };

    const handleClear = () => {
        submittedValueRef.current = '';
        onChange('');
        onSubmit?.('');
        inputRef.current?.focus();
    };

    if (!isExpanded) {
        return (
            <ActionBarItem
                icon={MagnifyingGlassIcon}
                label={t('browseBar.search')}
                onClick={() => onExpandedChange(true)}
            />
        );
    }

    return (
        <motion.div
            initial={{ width: 90 }}
            animate={{ width: 240 }}
            transition={{
                duration: 0.22,
                ease: [0.16, 1, 0.3, 1],
            }}
            className="flex items-center gap-2 h-8 pr-3 rounded-md overflow-hidden max-w-40 @medium:max-w-none"
        >
            <MagnifyingGlassIcon className="size-4 text-grey-300 flex-shrink-0" />
            <input
                ref={inputRef}
                autoFocus
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={handleBlur}
                onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                        onExpandedChange(false);
                    }
                }}
                placeholder={placeholder || t('browseBar.searchPlaceholder')}
                className="flex-1 min-w-0 bg-transparent text-sm-medium text-grey-100 placeholder:text-grey-500 outline-none"
            />
            {value && (
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleClear}
                    className="text-grey-500 hover:text-grey-300 transition-colors flex-shrink-0"
                    aria-label={t('browseBar.clearSearch')}
                >
                    <XCircleIcon className="size-4" weight="fill" />
                </button>
            )}
        </motion.div>
    );
}
